import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

interface AppUsage {
  appTitle: string;
  usage: number;
}

export const LookupFulfillmentBreakdownCard = memo(
  function LookupFulfillmentBreakdownCard() {
    const client = useRequiredContext(RedoAdminRpcClientContext);

    const team = useRequiredContext(TeamContext);

    const [breakdownLoad, triggerBreakdownLoad] = useTriggerLoad(async () => {
      const result = await client.getFulfillmentBreakdown({
        teamId: team._id.toString(),
      });
      return result.breakdown;
    });

    const handleLoadBreakdown = () => {
      triggerBreakdownLoad();
    };

    const renderBreakdownItem = ({ appTitle, usage }: AppUsage) => {
      return (
        <Flex
          borderColor="secondary"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="sm"
          key={`${appTitle}-usage`}
          p="md"
          radius="md"
        >
          <Flex dir="row" gap="sm">
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              {appTitle}:
            </Text>
            <Text fontSize="sm" textColor="primary">
              {usage}
            </Text>
          </Flex>
        </Flex>
      );
    };

    return (
      <Card
        subtitle="This will compile the fulfillment services used in the last 50 fulfilled orders"
        title="Lookup Fulfillment Breakdown"
      >
        <Flex dir="column" gap="md">
          <Flex dir="row">
            <RedoButton
              hierarchy="primary"
              onClick={handleLoadBreakdown}
              pending={breakdownLoad.pending}
              text="Load fulfillment breakdown"
              theme="success"
            />
          </Flex>

          {breakdownLoad.value && (
            <Flex dir="column" gap="md">
              {breakdownLoad.value.length > 0 ? (
                breakdownLoad.value.map(renderBreakdownItem)
              ) : (
                <Text fontSize="sm" textColor="secondary">
                  No external fulfillment events found. Are they fulfilling with
                  Redo?
                </Text>
              )}
            </Flex>
          )}

          {breakdownLoad.error && (
            <Text fontSize="sm" textColor="error">
              Error loading breakdown: {breakdownLoad.error.message}
            </Text>
          )}
        </Flex>
      </Card>
    );
  },
);
